/**
 * Super Admin — Blog CMS
 * Manage Knowledge Hub posts + moderate user comments (Bosco Guardian queue).
 * Protected: super_admin only.
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Plus, Edit3, Trash2, Eye, RefreshCw, Loader2, Save, X,
  Shield, CheckCircle2, AlertTriangle, MessageSquare,
  ThumbsUp, ThumbsDown, Bookmark,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { blogApi } from '../services/blogApi';

const STATUS_BADGE = {
  draft:     'bg-slate-100 text-slate-700 border-slate-300',
  published: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  scheduled: 'bg-amber-100 text-amber-800 border-amber-300',
};

const EMPTY_POST = {
  title: '',
  slug: '',
  excerpt: '',
  body: '',
  coverImage: '',
  categorySlug: 'pedagogy',
  status: 'draft',
  featured: false,
  author: 'ActanonEdu Team',
};

export default function SuperAdminBlogPage() {
  const [tab, setTab] = useState('posts');
  return (
    <div className="space-y-6" data-testid="super-admin-blog-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-indigo-600" />
            Knowledge Hub CMS
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Author articles, manage categories, and review comments flagged by Bosco Guardian.
          </p>
        </div>
        <Link to="/blog" target="_blank">
          <Button variant="outline" size="sm" data-testid="blog-cms-view-public">
            <Eye className="h-4 w-4 mr-1.5" /> View public blog
          </Button>
        </Link>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="posts" data-testid="blog-cms-tab-posts">Posts</TabsTrigger>
          <TabsTrigger value="moderation" data-testid="blog-cms-tab-moderation">
            <Shield className="h-4 w-4 mr-1.5" /> Moderation Queue
          </TabsTrigger>
          <TabsTrigger value="categories" data-testid="blog-cms-tab-categories">Categories</TabsTrigger>
        </TabsList>

        <TabsContent value="posts" className="mt-4"><PostsManager /></TabsContent>
        <TabsContent value="moderation" className="mt-4"><ModerationQueue /></TabsContent>
        <TabsContent value="categories" className="mt-4"><CategoriesManager /></TabsContent>
      </Tabs>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
//  Posts Manager
// ──────────────────────────────────────────────────────────
function PostsManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [editing, setEditing] = useState(null); // post obj or null
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await blogApi.admin.listPosts(statusFilter);
      setPosts(data.items);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing({ ...EMPTY_POST }); setOpen(true); };
  const openEdit = async (p) => {
    try {
      const full = await blogApi.admin.getPost(p.postId);
      setEditing(full);
      setOpen(true);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const removePost = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? (soft-delete)`)) return;
    try {
      await blogApi.admin.deletePost(p.postId);
      toast.success('Deleted.');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-40" data-testid="blog-cms-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={load} data-testid="blog-cms-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <Button onClick={openNew} data-testid="blog-cms-new-post">
          <Plus className="h-4 w-4 mr-1.5" /> New post
        </Button>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" /></div>
      ) : posts.length === 0 ? (
        <div className="py-12 text-center text-slate-500">No posts yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b">
              <tr>
                <th className="py-2 pr-3">Title</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Category</th>
                <th className="py-2 pr-3">Engagement</th>
                <th className="py-2 pr-3">Updated</th>
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => {
                const s = p.stats || {};
                const totalRatings = (s.helpful || 0) + (s.notHelpful || 0);
                const helpfulPct = totalRatings ? Math.round((100 * (s.helpful || 0)) / totalRatings) : null;
                return (
                <tr key={p.postId} className="border-b border-slate-100 dark:border-slate-800" data-testid={`blog-cms-row-${p.slug}`}>
                  <td className="py-2 pr-3">
                    <div className="font-medium">{p.title}</div>
                    <div className="text-xs text-slate-500">/{p.slug}</div>
                  </td>
                  <td className="py-2 pr-3">
                    <Badge variant="outline" className={STATUS_BADGE[p.status] || ''}>{p.status}</Badge>
                  </td>
                  <td className="py-2 pr-3">{p.categorySlug || '—'}</td>
                  <td className="py-2 pr-3">
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid={`blog-cms-stats-${p.slug}`}>
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800" title="Views">
                        <Eye className="h-3 w-3" /> {s.views || 0}
                      </span>
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300" title="Helpful votes">
                        <ThumbsUp className="h-3 w-3" /> {s.helpful || 0}
                      </span>
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-50 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300" title="Not helpful votes">
                        <ThumbsDown className="h-3 w-3" /> {s.notHelpful || 0}
                      </span>
                      {helpfulPct !== null && (
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold ${
                          helpfulPct >= 70 ? 'bg-emerald-100 text-emerald-900' :
                          helpfulPct >= 40 ? 'bg-amber-100 text-amber-900' :
                          'bg-rose-100 text-rose-900'
                        }`} title="Helpful percent">
                          {helpfulPct}%
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300" title="Bookmarks">
                        <Bookmark className="h-3 w-3" /> {s.bookmarks || 0}
                      </span>
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800" title="Approved comments">
                        <MessageSquare className="h-3 w-3" /> {s.comments || 0}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-slate-500">{(p.updatedAt || '').slice(0, 10)}</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`blog-cms-edit-${p.slug}`}>
                      <Edit3 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => removePost(p)} data-testid={`blog-cms-delete-${p.slug}`}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <PostEditorDialog
          open={open}
          onClose={() => { setOpen(false); setEditing(null); }}
          post={editing}
          onSaved={() => { setOpen(false); setEditing(null); load(); }}
        />
      )}
    </Card>
  );
}

// ──────────────────────────────────────────────────────────
//  Post Editor Dialog
// ──────────────────────────────────────────────────────────
function PostEditorDialog({ open, onClose, post, onSaved }) {
  const [form, setForm] = useState(post);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [cats, setCats] = useState([]);

  useEffect(() => { setForm(post); }, [post]);

  useEffect(() => {
    blogApi.listCategories().then((d) => setCats(d.items)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error('Title and body are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        slug: form.slug || undefined,
        excerpt: form.excerpt,
        body: form.body,
        coverImage: form.coverImage || null,
        categorySlug: form.categorySlug,
        status: form.status,
        featured: Boolean(form.featured),
        author: form.author || 'ActanonEdu Team',
      };
      if (form.postId) {
        await blogApi.admin.updatePost(form.postId, payload);
        toast.success('Post updated.');
      } else {
        await blogApi.admin.createPost(payload);
        toast.success('Post created.');
      }
      onSaved();
    } catch (e) {
      toast.error(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{form.postId ? 'Edit post' : 'New post'}</DialogTitle>
          <DialogDescription>Markdown body supported. Use # for H1, ## for H2, **bold**, _italic_, lists, etc.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-500">Title</label>
            <Input
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              data-testid="blog-cms-field-title"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-500">Slug (auto if blank)</label>
            <Input
              value={form.slug || ''}
              onChange={(e) => set('slug', e.target.value)}
              placeholder="my-article"
              data-testid="blog-cms-field-slug"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <label className="text-xs font-medium text-slate-500">Excerpt (1–2 sentences)</label>
            <Textarea
              rows={2}
              value={form.excerpt || ''}
              onChange={(e) => set('excerpt', e.target.value)}
              data-testid="blog-cms-field-excerpt"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-500">Category</label>
            <Select value={form.categorySlug || ''} onValueChange={(v) => set('categorySlug', v)}>
              <SelectTrigger data-testid="blog-cms-field-category"><SelectValue placeholder="Choose category" /></SelectTrigger>
              <SelectContent>
                {cats.map((c) => (
                  <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-500">Status</label>
            <Select value={form.status} onValueChange={(v) => set('status', v)}>
              <SelectTrigger data-testid="blog-cms-field-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-2">
            <label className="text-xs font-medium text-slate-500">Cover image URL (optional)</label>
            <Input
              value={form.coverImage || ''}
              onChange={(e) => set('coverImage', e.target.value)}
              placeholder="https://..."
              data-testid="blog-cms-field-cover"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-500">Author</label>
            <Input
              value={form.author || ''}
              onChange={(e) => set('author', e.target.value)}
              data-testid="blog-cms-field-author"
            />
          </div>
          <div className="space-y-2 flex items-center pt-6">
            <label className="text-sm flex items-center gap-2">
              <input
                type="checkbox"
                checked={Boolean(form.featured)}
                onChange={(e) => set('featured', e.target.checked)}
                data-testid="blog-cms-field-featured"
              />
              Featured
            </label>
          </div>

          <div className="space-y-2 md:col-span-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-slate-500">Body (Markdown)</label>
              <Button variant="ghost" size="sm" onClick={() => setShowPreview((s) => !s)} data-testid="blog-cms-toggle-preview">
                {showPreview ? 'Hide preview' : 'Show preview'}
              </Button>
            </div>
            <div className={`grid ${showPreview ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
              <Textarea
                rows={16}
                value={form.body}
                onChange={(e) => set('body', e.target.value)}
                className="font-mono text-sm"
                data-testid="blog-cms-field-body"
              />
              {showPreview && (
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-4 prose prose-slate dark:prose-invert max-w-none overflow-y-auto max-h-[400px]">
                  <ReactMarkdown>{form.body || '*Nothing to preview*'}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="blog-cms-cancel">
            <X className="h-4 w-4 mr-1.5" /> Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="blog-cms-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
            {form.postId ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────
//  Moderation Queue
// ──────────────────────────────────────────────────────────
function ModerationQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await blogApi.admin.moderationQueue(status);
      setItems(data.items);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      await blogApi.admin.approveComment(id);
      toast.success('Approved.');
      load();
    } catch (e) { toast.error(e.message); }
  };
  const reject = async (id) => {
    try {
      await blogApi.admin.rejectComment(id);
      toast.success('Rejected.');
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44" data-testid="blog-cms-mod-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={load} data-testid="blog-cms-mod-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <div className="text-xs text-slate-500 flex items-center gap-1.5">
          <Shield className="h-4 w-4 text-indigo-600" /> Bosco Guardian (Claude Sonnet 4.6)
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-slate-500">No comments in this state.</div>
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <div key={c.commentId} className="border border-slate-200 dark:border-slate-800 rounded-lg p-3" data-testid={`blog-cms-mod-${c.commentId}`}>
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <div className="font-semibold text-sm">{c.authorName}</div>
                <div className="text-xs text-slate-500">{(c.createdAt || '').slice(0, 10)}</div>
              </div>
              <p className="text-sm whitespace-pre-wrap">{c.body}</p>
              {c.moderation && (
                <div className="mt-2 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                  <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {c.moderation.decision} · {c.moderation.severity || 'low'}
                  </Badge>
                  <span>{c.moderation.reason}</span>
                  {(c.moderation.categories || []).map((cat) => (
                    <Badge key={cat} variant="outline" className="text-xs">{cat}</Badge>
                  ))}
                </div>
              )}
              {status === 'pending' && (
                <div className="mt-3 flex gap-2">
                  <Button size="sm" onClick={() => approve(c.commentId)} data-testid={`blog-cms-approve-${c.commentId}`}>
                    <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => reject(c.commentId)} data-testid={`blog-cms-reject-${c.commentId}`}>
                    <X className="h-4 w-4 mr-1.5" /> Reject
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ──────────────────────────────────────────────────────────
//  Categories Manager
// ──────────────────────────────────────────────────────────
function CategoriesManager() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await blogApi.listCategories();
      setCats(d.items);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      await blogApi.admin.createCategory({ name, slug: slug || undefined, description });
      toast.success('Category created.');
      setName(''); setSlug(''); setDescription('');
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card className="p-4 space-y-4">
      <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="blog-cms-new-category-form">
        <Input placeholder="Category name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="blog-cms-cat-name" />
        <Input placeholder="Slug (auto if blank)" value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="blog-cms-cat-slug" />
        <Input placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="blog-cms-cat-desc" />
        <Button type="submit" disabled={creating} data-testid="blog-cms-cat-create">
          {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
          Add category
        </Button>
      </form>

      {loading ? (
        <div className="py-8 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin text-slate-400" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {cats.map((c) => (
            <div key={c.slug} className="border rounded-lg p-3" data-testid={`blog-cms-cat-row-${c.slug}`}>
              <div className="font-semibold">{c.name}</div>
              <div className="text-xs text-slate-500">/{c.slug}</div>
              {c.description && <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{c.description}</p>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
